import React from 'react';
import { Chip, FormControl, MenuItem, Select } from '@mui/material';
import { brandColors } from '@config/brandTokens';
import {
  actingPlayerLabel,
  type ContestActingPlayer,
} from '@hooks/useContestActingPlayer';

interface ContestActingPlayerSwitchProps {
  selected: ContestActingPlayer | null;
  options: ContestActingPlayer[];
  canSwitch: boolean;
  onChange: (playerId: string) => void;
  accent?: string;
}

const ContestActingPlayerSwitch: React.FC<ContestActingPlayerSwitchProps> = ({
  selected,
  options,
  canSwitch,
  onChange,
  accent,
}) => {
  const color = accent || brandColors.navy;
  const tint = accent ? `${accent}14` : brandColors.white;
  if (!selected) return null;
  if (!canSwitch) {
    return (
      <Chip
        size="small"
        label={actingPlayerLabel(selected)}
        sx={{
          fontWeight: 800,
          bgcolor: tint,
          color,
          border: `1px solid ${color}55`,
        }}
      />
    );
  }

  return (
    <FormControl size="small" sx={{ minWidth: { xs: 160, sm: 200 } }}>
      <Select
        value={selected.playerId}
        onChange={(event) => onChange(String(event.target.value))}
        sx={{
          bgcolor: tint,
          color,
          fontWeight: 700,
          fontSize: '0.82rem',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: `${color}66` },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: color },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: color },
          '& .MuiSelect-select': { py: 0.7, px: 1.1 },
          '& .MuiSvgIcon-root': { color },
        }}
      >
        {options.map((option) => (
          <MenuItem key={option.playerId} value={option.playerId} sx={{ fontSize: '0.85rem' }}>
            {option.kind === 'self' ? `${option.displayName} (you)` : option.displayName}
            {option.missingDob
              ? ' · Add date of birth'
              : option.ageCategory
                ? ` · ${option.ageCategory}`
                : ''}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default ContestActingPlayerSwitch;
